import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode, useState } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetPracticeAreaByIdQuery, useUpdatePracticeAreaMutation } from 'store/api/practice-areas';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { PracticeArea, PracticeAreaForm } from 'types/practice-areas';
import { useClassifiers } from 'utils/useClassifiers';
import {
    PRACTICE_AREA_SECTION_FIELDS,
    PracticeAreaSectionId,
    practiceAreaDtoToForm,
    practiceAreaFormToRequest,
} from '../practiceAreaFormMapping';
import { PracticeAreaFields, PracticeAreaVisibilityFields } from './PracticeAreaFields';
import { PRACTICE_AREA_FORM_CLASSIFIERS, PracticeAreaFormState, usePracticeAreaForm } from './usePracticeAreaForm';

const SECTION_FIELDS: Record<PracticeAreaSectionId, (form: PracticeAreaFormState) => ReactNode> = {
    'practice-area': (form) => <PracticeAreaFields form={form} />,
    visibility: (form) => <PracticeAreaVisibilityFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

interface SectionFormProps {
    section: PracticeAreaSectionId;
    initialData: PracticeAreaForm;
    customFields: CustomField[] | undefined;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

function PracticeAreaSectionForm({ section, initialData, customFields, classifiers, onClose }: SectionFormProps) {
    // The page's own query argument, so the refetch refreshes the practice area the page shows.
    const { id: practiceAreaId = '' } = useParams();
    const { refetch } = useGetPracticeAreaByIdQuery(practiceAreaId);
    const [updatePracticeArea] = useUpdatePracticeAreaMutation();
    const form = usePracticeAreaForm({ initialData, classifiers, entityCustomFields: customFields });

    const onSave = (values: PracticeAreaForm) =>
        saveSection(values, {
            section,
            sectionFields: PRACTICE_AREA_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (practiceArea) => ({
                ...practiceAreaDtoToForm(practiceArea),
                ...form.customFieldValuesOf(practiceArea.customFields),
            }),
            toRequest: (edited) =>
                practiceAreaFormToRequest(
                    edited,
                    form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>)
                ),
            update: (request) => updatePracticeArea({ ...request, id: practiceAreaId }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: PracticeAreaSectionId;
    practiceArea: PracticeArea;
    onClose: () => void;
}

function PracticeAreaSectionEditor({ section, practiceArea, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: PRACTICE_AREA_FORM_CLASSIFIERS,
    });
    // Frozen at open, custom fields included (the form takes their values from them), so the refetches around a
    // save do not reset the fields being edited.
    const [frozen] = useState(() => ({
        initialData: practiceAreaDtoToForm(practiceArea),
        customFields: practiceArea.customFields,
    }));

    if (isLoadingClassifiers) {
        return <Loader />;
    }
    return (
        <PracticeAreaSectionForm
            section={section}
            initialData={frozen.initialData}
            customFields={frozen.customFields}
            classifiers={classifiers}
            onClose={onClose}
        />
    );
}

interface PracticeAreaSectionProps {
    id: PracticeAreaSectionId;
    titleId: string;
    practiceArea: PracticeArea;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
}

/** A card of the practice area page that edits in place with the practice area form's fields for that card. */
export function PracticeAreaSection({ id, titleId, practiceArea, children }: PracticeAreaSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <PracticeAreaSectionEditor section={id} practiceArea={practiceArea} onClose={close} />}
        >
            {children}
        </SectionEdit>
    );
}
